import { Contact, Link, QrCode, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import DataTablePagination from "../../DataTablePagination";
import Spacing from "../../Spacing";
import { getPractice, numberWithCommas, generatePatientIntakeTableData,  handleQRScanPage, handelCopyLink, debounce } from "../../../helpers";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import { fetchPatientIntakeForm } from "../../../services/forms";
import PatientIntakeDataFormTable from "../../DataTable/PatientIntakeDataFormTable";
import FormViewDetailsModal from "../../Modal/FormViewDetailsModal";


export default function PatientIntakeForm({
  selectedPracticeChange,
  fetchChangePracticeData,
}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if (selectedPracticeChange != null && fetchChangePracticeData) {
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [formViewId, setFormViewId] = useState(null)
  const [formSize, setFormSizes] = useState('A4')
  const [formView, setFormView] = useState(false)
  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filter, setFilter] = useState({
    account_id: account_id,
    location_id: location_id,
    page: 1,
    per_page: 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    sex: "",
    option2_tel_no: "",
    order_by: [],
  });

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchPatientIntakeForm(getFilter);
      if (response) {
        const generateData = generatePatientIntakeTableData(response.data.results);
        setData(generateData);
        setDataCount({
          totalCount: response.data.count,
          totalPages: response.data.total_pages,
        });
        if (checkedIds.length < 1) {
          setSelectAll(false);
        }
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
  }, [location_id, account_id]);

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  const handleCheckboxChange = (id) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
  };

  const handleSelectAll = () => {
    const newCheckedIds = selectAll ? [] : data.map((item) => item.id);
    setCheckedIds(newCheckedIds);

    const newData = data.map((row) => ({
      ...row,
      checked: !selectAll,
    }));
    setData(newData);
    setSelectAll(!selectAll);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  const handleFormView = (formId) => {
    setFormViewId(formId)
    setFormView(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  };
  const handleFormPrint = (formId) => {
    const url = `/forms/patient-intake-form/print?form_id=${formId}&size=A4`;
    window.open(url, '_blank');
  };

  useDocumentTitle("Patient Intake Form");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i className="docpro_teal_bg docpro_white_color">
            <Contact width={20} height={20} />
          </i>
          Patient Intake Form
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <div className="flex items-center">
            <form className="docpro_search_form width_100">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>

          <div className="docpro_data_table_btns p-0">
            {(getPractice("website") !== null && getPractice("website")) && (
              <>
                <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={()=>handleQRScanPage('patient_intake')}
                >
                  <QrCode width={20} height={20} />
                  Form QR code
                </button>
                <button 
                  className="docpro_bordered_btn docpro_fw_600 docpro_primary_color"
                  onClick={()=>handelCopyLink(`${getPractice("website")}/patient-intake-form`)}
                >
                  <Link width={20} height={20} />
                  Form link
                </button>
              </>
            )}
          </div>
        </div>
        <PatientIntakeDataFormTable
          tableData={data}
          handleSelectAll={handleSelectAll}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          checkSelected={checkSelected}
          handleFormView={handleFormView}
          handleFormPrint={handleFormPrint}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />
        {data.length > 0 && !dataLoading && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {formView && (
        <FormViewDetailsModal 
          formId = {formViewId}
          formSize = {formSize}
          formView = {formView}
          setFormView = {setFormView}
          formType = {'patient_intake'}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
